import * as React from 'react'
import {
  Variant,
  withUniqueApiNames,
  type Block,
  type VariantSetBlock,
} from '@basehub/data/replicache/index'
import { PlusIcon } from '@radix-ui/react-icons'
import { Button } from '~/sotano-kit/primitives/button'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getId } from '@basehub/utils/id/index'
import { Input } from '~/sotano-kit/primitives/input'
import { clsx } from 'clsx'
import { useVariantSetStore } from './store'
import { COLORS, getSolidColor } from './utils'
import throttle from 'lodash.throttle'
import { useEditorStore } from '../../editor-store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { toast } from 'sonner'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { RightIcon } from '~/components/icons/right-icon'
import { DocActionDialog } from '~/sotano-kit/primitives/confirmation-dialog'
import { useToggleState } from '~/hooks/use-toggle-state'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'

export const VariantSetBlockInput = ({
  block,
  path,
}: {
  block: VariantSetBlock
  path: string
  parent: Block
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const [isNewVariant, setIsNewVariant] = React.useState<boolean>(false)
  const { focusedVariantId, setFocusedVariantId } = useVariantSetStore((s) => ({
    focusedVariantId: s.focusedVariantId,
    setFocusedVariantId: s.setFocusedVariantId,
  }))
  const undoManager = useRepoUndoManager()

  const handleChange = (value: string, variantId: string) =>
    replicache?.mutate.updateBlock({
      id: block.id,
      path,
      strategy: 'merge-deep',
      isoNow: new Date().toISOString(),
      instanceChildTarget: block.instanceChildTarget,
      data: {
        value: {
          variants: block.value.variants.map((v) => {
            if (v.id === variantId) {
              return {
                ...v,
                label: value,
                apiName: '',
              } satisfies Variant
            }
            return v
          }),
        },
      },
    })

  const containerRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (focusedVariantId && containerRef.current) {
      const active = containerRef.current.querySelector(
        `button[data-variant-item][data-active="true"]`
      ) as HTMLButtonElement | null

      active?.focus()
    }
  }, [focusedVariantId])

  const variants = React.useMemo(
    () =>
      withUniqueApiNames(block.value.variants).map((v, i) => ({
        ...v,
        originalLabel: block.value.variants[i]?.label,
      })),
    [block.value.variants]
  )

  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const deleteVariantToggleState = useToggleState()
  const selectedVariant = variants.find((v) => v.id === focusedVariantId)
  const getInputFocusOp = React.useCallback(
    (id: string | undefined) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:value',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const sourceBlockId = idToSourceBlockId(block)
  const isBlockSelected = useEditorStore(
    (s) => s.selectedBlockId === sourceBlockId
  )
  React.useEffect(() => {
    if (!isBlockSelected) {
      setFocusedVariantId(null)
    }
  }, [isBlockSelected, setFocusedVariantId])

  return (
    <div
      ref={containerRef}
      className="border border-dark-border-solid rounded-md py-[1.125rem] px-[1.375rem] bg-dark-base-dark grid gap-y-3"
    >
      {variants.map((variant) =>
        isNewVariant && focusedVariantId === variant.id ? (
          <div key={variant.id} className="flex gap-x-2 items-center">
            <div
              className="size-[0.625rem] rounded-full"
              style={{ backgroundColor: getSolidColor(variant.color) }}
            />
            <Input
              iconPaddingLeft={false}
              containerClassName="[&>span:first-child]:right-2 [&>span:first-child]:!left-auto w-fit"
              icon={<RightIcon className="text-dark-muted" />}
              autoFocus
              value={variant.originalLabel}
              onBlur={(e) => {
                handleChange((e.target as HTMLInputElement).value, variant.id)
                setIsNewVariant(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleChange((e.target as HTMLInputElement).value, variant.id)
                  setIsNewVariant(false)
                }
              }}
              onChange={throttle(
                (e) => handleChange(e.target.value, variant.id),
                500
              )}
            />
          </div>
        ) : (
          <VariantDisplay
            key={variant.id}
            {...variant}
            blockId={sourceBlockId}
            isBlockSelected={isBlockSelected}
            onDelete={deleteVariantToggleState.handleOn}
            setVariantId={setFocusedVariantId}
            selectedVariantId={focusedVariantId}
          />
        )
      )}
      <Button
        unstyled
        focusStyled
        onFocus={() => setFocusedVariantId(null)}
        className="text-dark-faint text-sm flex gap-x-1 items-center leading-normal hover:text-dark-dim transition-colors cursor-default !outline-offset-8"
        onClick={() => {
          const newVariantId = getId()
          replicache?.mutate.updateBlock({
            data: {
              value: {
                variants: [
                  ...block.value.variants,
                  {
                    id: newVariantId,
                    label: '',
                    apiName: '',
                    isDefault: block.value.variants.length === 0,
                    hidden: false,
                    color:
                      COLORS[block.value.variants.length % COLORS.length] ??
                      'var(--presence-raw-blue)',
                  } satisfies Variant,
                ],
              },
            },
            id: block.id,
            instanceChildTarget: block.instanceChildTarget,
            path,
            strategy: 'merge-deep',
            isoNow: new Date().toISOString(),
          })

          setIsNewVariant(true)
          setFocusedVariantId(newVariantId)
        }}
      >
        <PlusIcon />
        Add variant
      </Button>
      <DocActionDialog
        title={`Are you sure you want to delete ${selectedVariant?.label} from ${block.title}?`}
        description="Deleting the variant will remove all block overrides that were made for this variant in particular."
        open={deleteVariantToggleState.isOn}
        handleClose={deleteVariantToggleState.handleToggle}
        action={async () => {
          if (!selectedVariant || selectedVariant?.isDefault) return
          setTimeout(() => {
            const currIndex = block.value.variants.findIndex(
              (v) => v.id === selectedVariant.id
            )
            setFocusedVariantId(
              block.value.variants[currIndex + 1]?.id ??
                block.value.variants[currIndex - 1]?.id ??
                null
            )
          }, 10)

          const params: UpdateBlockParams = {
            id: block.id,
            path,
            strategy: 'merge-deep',
            instanceChildTarget: block.instanceChildTarget,
            data: {
              type: block.type,
              value: {
                variants: block.value.variants.filter(
                  (variant) => variant.id !== selectedVariant.id
                ),
              },
            },
            isoNow: new Date().toISOString(),
          }
          const result = await replicache?.mutate
            .updateBlock(params)
            .then((r) => {
              toast.success(`Variant "${selectedVariant.label}" removed`)
              return r
            })
            .catch((e) => {
              if (e instanceof MutatorUserError) {
                toast.error(e.message)
              } else {
                toast.error('An error occurred while deleting the variant')
              }
            })

          if (result?.undo) {
            undoManager.current?.push({
              redo: [
                getInputFocusOp(
                  `${idToSourceBlockId(block)}-pp-remove-variant`
                ),
                { op: 'updateBlock', args: params },
              ],
              undo: [
                getInputFocusOp(undefined),
                {
                  op: 'blockBatchMutations',
                  args: { mutations: result.undo },
                },
              ],
            })
          }
        }}
      />
    </div>
  )
}

const VariantDisplay = ({
  blockId,
  setVariantId,
  selectedVariantId,
  onDelete,
  isBlockSelected,
  ...props
}: Variant & {
  blockId: VariantSetBlock['id']
  onDelete: VoidFunction
  isBlockSelected: boolean
  selectedVariantId: string | null
  setVariantId: (id: string | null) => void
}) => {
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)

  const handleFocus = React.useCallback(() => {
    setVariantId(props.id)
    setSelectedBlockId(blockId)
  }, [blockId, props.id, setSelectedBlockId, setVariantId])

  return (
    <button
      onKeyDown={(e) => {
        if (e.metaKey || e.shiftKey || e.ctrlKey) return
        if (e.key === 'Backspace') {
          if (props.isDefault) {
            toast.error('Cannot delete the default variant')
            return
          }
          e.preventDefault()
          onDelete()
        }
      }}
      data-variant-item
      data-active={selectedVariantId === props.id}
      className={clsx(
        'grid grid-cols-[auto_1fr] items-center gap-x-2 rounded-[1px] cursor-default text-left outline-offset-8 focus-visible:outline-dark-control-base',
        selectedVariantId === props.id &&
          isBlockSelected &&
          'outline outline-1 outline-dark-control-base',
        props.hidden && 'opacity-50'
      )}
      onFocus={handleFocus}
      onClick={handleFocus}
    >
      <div
        className={clsx(
          'size-[0.625rem] rounded-full',
          props.isDefault && '!bg-dark-elevated'
        )}
        style={{ backgroundColor: getSolidColor(props.color) }}
      />
      <span className="font-medium text-sm min-h-[1.3125rem] flex items-center">
        {props.label}
        {props.isDefault && (
          <span className="ml-1.5 text-[0.6875rem] px-1 bg-dark-shade-hover inline-block leading-[1.125rem] rounded text-dark-faint uppercase font-medium">
            default
          </span>
        )}
      </span>
      <span className="col-start-2 text-dark-faint text-sm leading-normal">
        {props.apiName}
      </span>
    </button>
  )
}
